import { useCallback } from 'react';

import { resolveHitlTask, classifyHitlResolveError } from '../services/hitlTasks.js';
import { buildDocumentPickDecision } from '../utils/hitlDecision.js';
import { markHitlContractDismissed } from '../utils/dismissedHitlContracts.js';

/**
 * Shared resolver for a document-pick (`hitl_pick`) gate.
 *
 * Both resolve paths land here so they behave identically after the decision:
 * - AIMS Drive pick → `documentUuid`
 * - Local file attach → `fileUuid` (+ name / file_type)
 *
 * On success the contract is dismissed (so the durable poll never re-seeds it)
 * and the card slot is cleared; the discovery workflow then continues (a new
 * card may follow). `already_resolved` / `resume_conflict` are treated as soft
 * success (clear + info toast); retryable errors keep the card so the human
 * can try again.
 *
 * @param {{
 *   apiClient: import('axios').AxiosInstance,
 *   setHitlRequest: (v: object|null) => void,
 *   toast: { success: Function, info: Function, error: Function },
 *   t: (key: string) => string,
 *   onResumeStart?: () => void,
 * }} params
 * @param {() => void} [params.onResumeStart] — Called right after the decision is
 *   accepted and the workflow resume is requested. The resume path emits no
 *   lifecycle feedback events, so the caller uses this to optimistically show a
 *   "working" indicator until the next event (next gate / completion) arrives.
 * @returns {(args: {
 *   card: { interactionId: string, gate?: string },
 *   documentUuid?: string,
 *   fileUuid?: string,
 *   name?: string,
 *   fileType?: string,
 * }) => Promise<boolean>}  Resolves to `true` when the gate was cleared.
 */
export const useResolvePickGate = ({ apiClient, setHitlRequest, toast, t, onResumeStart }) =>
  useCallback(
    async ({ card, documentUuid, fileUuid, name, fileType }) => {
      if (!card?.interactionId) return false;
      try {
        await resolveHitlTask(apiClient, {
          contractId: card.interactionId,
          decision: buildDocumentPickDecision({
            gate: card.gate,
            documentUuid,
            fileUuid,
            name,
            fileType,
          }),
        });
        // Dismiss first (and deny re-seed) so a throwing toast can never strand
        // the card and the durable poll never resurrects a resolved contract.
        markHitlContractDismissed(card.interactionId);
        setHitlRequest?.(null);
        // The workflow now resumes but (unlike the initial send) emits no
        // lifecycle events during processing — show a working indicator so the
        // chat isn't dead until the next gate / completion arrives.
        onResumeStart?.();
        toast.success({
          title: t('chat.hitl.documentSelected'),
          message: t('chat.hitl.documentSelectedDetail'),
        });
        return true;
      } catch (err) {
        const { kind } = classifyHitlResolveError(err);
        if (kind === 'already_resolved') {
          markHitlContractDismissed(card.interactionId);
          setHitlRequest?.(null);
          toast.info({
            title: t('chat.hitl.alreadyResolved'),
            message: t('chat.hitl.alreadyResolvedDetail'),
          });
          return true;
        }
        if (kind === 'resume_conflict') {
          markHitlContractDismissed(card.interactionId);
          setHitlRequest?.(null);
          toast.info({
            title: t('chat.hitl.resumeConflict'),
            message: t('chat.hitl.resumeConflictDetail'),
          });
          return true;
        }
        // Retryable (network / 5xx): keep the card so the human can retry.
        toast.error({
          title: t('chat.hitl.failed'),
          message: err?.response?.data?.detail || err?.message || String(err),
        });
        return false;
      }
    },
    [apiClient, setHitlRequest, toast, t, onResumeStart],
  );

export default useResolvePickGate;
